import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AuthAccount } from '@/types/auth';

interface AccountSelectionPanelProps {
  availableAccounts: AuthAccount[];
  isSelectingAccount: boolean;
  onSelectAccount: (accountId: string) => void;
}

export function AccountSelectionPanel({
  availableAccounts,
  isSelectingAccount,
  onSelectAccount,
}: AccountSelectionPanelProps) {
  const { t } = useTranslation('entity-portal');

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 1.5,
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        {t('auth.selectAccountTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('auth.selectAccountDescription')}
      </Typography>
      <List disablePadding aria-label={t('auth.selectAccountTitle')}>
        {availableAccounts.map((account) => (
          <ListItem key={account.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => onSelectAccount(account.id)}
              disabled={isSelectingAccount}
              sx={{
                borderRadius: 1.5,
                border: '1px solid transparent',
                '&:hover': {
                  borderColor: 'divider',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <ListItemText
                primary={account.displayName || account.username}
                secondary={account.email || account.username}
                slotProps={{
                  primary: { variant: 'subtitle1' },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
